/**
 * WorkflowMachineContext — the residue of the original Athena workflow
 * god-context (Modularity Epic 4 / Story 4.6).
 *
 * Owns the workflow state-machine fields (workflowState, workflowContext),
 * UI flags (showReviewPanel, showStepComponent, showReportGenerator,
 * currentReviewData), the residual `isRevising` flag (per audit §1.5),
 * and the orchestration glue: the dispatch bridge, derived selectors in
 * contextValue, and the action-creators that span multiple slices.
 *
 * Slice ownership (post Stories 4.2–4.6):
 *   - Session   → WorkflowSessionContext
 *   - Messages  → WorkflowMessagesContext
 *   - Results   → WorkflowResultsContext
 *   - Revision  → WorkflowRevisionContext (revisionAgent + revisionStep)
 *   - Machine   → this file (state machine + UI flags + orchestration)
 *
 * Heavy bodies extracted to hooks/utils during Story 4.6 wrap-up:
 *   - hooks/useSessionRestoration.js     (~950 LOC restoreInProgressSession)
 *   - hooks/useStatusPolling.js          (~150 LOC polling useEffect)
 *   - hooks/useSessionPersistenceSync.js (~65 LOC persistence sync)
 *   - contexts/_dispatchBridge.js        (~80 LOC bridge factory)
 *   - utils/stepMapping.js               (~35 LOC ISO 21434 mappings)
 */

import React, { createContext, useContext, useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { WorkflowStateMachine } from '../state/WorkflowStateMachine';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useRevisionWorkflow } from '../hooks/useRevisionWorkflow';
import { config } from '../../../config/env';
import { WORKFLOW_STATES } from '../../../config/athenaWorkflowConfig';
import { WorkflowSessionProvider, useWorkflowSession } from './WorkflowSessionContext';
import { WorkflowMessagesProvider, useWorkflowMessages } from './WorkflowMessagesContext';
import { WorkflowResultsProvider, useWorkflowResults } from './WorkflowResultsContext';
import { WorkflowRevisionProvider, useWorkflowRevision } from './WorkflowRevisionContext';
import { createDispatchBridge } from './_dispatchBridge';
// Story 4.6 wrap-up: helpers + restoration logic + status polling all
// extracted out of the residue. The residue retains only orchestration
// concerns (reducer, action creators, contextValue assembly).
// Story 4.6 wrap-up: status polling extracted to hooks/useStatusPolling.js
import { useStatusPolling } from '../hooks/useStatusPolling';
// Story 4.6 wrap-up: ~950-line restoreInProgressSession extracted to a hook.
import { useSessionRestoration } from '../hooks/useSessionRestoration';
// Story 4.6 wrap-up: persistence restore + save useEffects extracted to a hook.
import { useSessionPersistenceSync } from '../hooks/useSessionPersistenceSync';

// Context
const WorkflowMachineContext = createContext(null);

// Action types
// Exported (Story 4.6 wrap-up) so extracted hooks can dispatch via the bridge
// without losing the centralized enum. The bridge in
// WorkflowMachineProviderInner routes each action.type to the correct slice.
export const ACTIONS = {
  // Workflow state
  SET_WORKFLOW_STATE: 'SET_WORKFLOW_STATE',
  UPDATE_WORKFLOW_CONTEXT: 'UPDATE_WORKFLOW_CONTEXT',

  // Session data
  UPDATE_SESSION_DATA: 'UPDATE_SESSION_DATA',
  SET_DOCUMENTS: 'SET_DOCUMENTS',
  SET_ITEM_DEFINITION: 'SET_ITEM_DEFINITION',
  SET_ASSETS: 'SET_ASSETS',
  SET_THREATS: 'SET_THREATS',
  SET_DAMAGE_SCENARIOS: 'SET_DAMAGE_SCENARIOS',
  SET_ATTACK_PATHS: 'SET_ATTACK_PATHS',
  SET_RISKS: 'SET_RISKS',
  SET_CYBERSECURITY_GOALS: 'SET_CYBERSECURITY_GOALS',
  SET_CYBERSECURITY_CLAIMS: 'SET_CYBERSECURITY_CLAIMS',
  SET_CYBERSECURITY_CONTROLS: 'SET_CYBERSECURITY_CONTROLS',
  SET_REQUIREMENTS: 'SET_REQUIREMENTS',
  SET_REPORT: 'SET_REPORT',

  // Messages
  ADD_MESSAGE: 'ADD_MESSAGE',
  UPDATE_MESSAGE: 'UPDATE_MESSAGE',
  CLEAR_MESSAGES: 'CLEAR_MESSAGES',

  // UI state
  SET_SHOW_REVIEW_PANEL: 'SET_SHOW_REVIEW_PANEL',
  SET_SHOW_STEP_COMPONENT: 'SET_SHOW_STEP_COMPONENT',
  SET_SHOW_REPORT_GENERATOR: 'SET_SHOW_REPORT_GENERATOR',

  // Backend session
  SET_BACKEND_SESSION: 'SET_BACKEND_SESSION',
  UPDATE_BACKEND_SESSION: 'UPDATE_BACKEND_SESSION',
  SET_BACKEND_SESSIONS: 'SET_BACKEND_SESSIONS',
  SET_LOADING_BACKEND_SESSIONS: 'SET_LOADING_BACKEND_SESSIONS',
  SET_BACKEND_SESSIONS_ERROR: 'SET_BACKEND_SESSIONS_ERROR',
  SET_TENANT_ID: 'SET_TENANT_ID',

  // Revision
  SET_REVISION_STATE: 'SET_REVISION_STATE',

  // Reset
  RESET_WORKFLOW: 'RESET_WORKFLOW',
  CLEAR_SESSION_FOR_RESTORATION: 'CLEAR_SESSION_FOR_RESTORATION'
};

// Initial state
export const initialState = {
  // Workflow state machine
  workflowState: WORKFLOW_STATES.IDLE,
  workflowContext: {},

  // Session data — MIGRATED to WorkflowResultsContext (Story 4.4).
  // The entire `sessionData` namespace (per-agent results +
  // hierarchical TARA + runtime fields) lives there now. The
  // god-context residue reads it via useWorkflowResults() to compute
  // derived selectors (workflowStatus / currentAgent / etc.).

  // Messages — MIGRATED to WorkflowMessagesContext (Story 4.3).

  // UI state
  showReviewPanel: false,
  showStepComponent: false,
  showReportGenerator: false,
  currentReviewData: null,

  // Backend session — MIGRATED to WorkflowSessionContext (Story 4.2).
  // Fields owned by Session: backendSession, backendSessions,
  // isLoadingBackendSessions, backendSessionsError, tenantId.

  // Revision state — Story 4.6: revisionAgent + revisionStep migrated
  // to WorkflowRevisionContext. `isRevising` stays here per audit §1.5
  // (it's a UI flag consumed by 9 step components; the other two are
  // write-only metadata consumed only by ReviewPanel).
  isRevising: false,
};

// Reducer
export function workflowMachineReducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_WORKFLOW_STATE:
      return {
        ...state,
        workflowState: action.payload.state,
        workflowContext: action.payload.context || state.workflowContext
      };

    case ACTIONS.UPDATE_WORKFLOW_CONTEXT:
      return {
        ...state,
        workflowContext: {
          ...state.workflowContext,
          ...action.payload
        }
      };

    // Results-slice cases (UPDATE_SESSION_DATA, SET_DOCUMENTS,
    // SET_ITEM_DEFINITION, SET_ASSETS, SET_THREATS,
    // SET_DAMAGE_SCENARIOS, SET_ATTACK_PATHS, SET_RISKS,
    // SET_CYBERSECURITY_GOALS, SET_CYBERSECURITY_CLAIMS,
    // SET_CYBERSECURITY_CONTROLS, SET_REQUIREMENTS, SET_REPORT)
    // MIGRATED to WorkflowResultsContext.jsx in Story 4.4.

    // Messaging cases (ADD_MESSAGE, UPDATE_MESSAGE, CLEAR_MESSAGES)
    // MIGRATED to WorkflowMessagesContext.jsx in Story 4.3.

    case ACTIONS.SET_SHOW_REVIEW_PANEL:
      return {
        ...state,
        showReviewPanel: action.payload.show,
        currentReviewData: action.payload.data || null
      };

    case ACTIONS.SET_SHOW_STEP_COMPONENT:
      return {
        ...state,
        showStepComponent: action.payload
      };

    case ACTIONS.SET_SHOW_REPORT_GENERATOR:
      return {
        ...state,
        showReportGenerator: action.payload
      };

    // Session-slice cases (SET_BACKEND_SESSION, UPDATE_BACKEND_SESSION,
    // SET_BACKEND_SESSIONS, SET_LOADING_BACKEND_SESSIONS,
    // SET_BACKEND_SESSIONS_ERROR, SET_TENANT_ID) MIGRATED to
    // WorkflowSessionContext.jsx in Story 4.2.

    case ACTIONS.SET_REVISION_STATE:
      // Story 4.6: revisionAgent + revisionStep migrated to
      // WorkflowRevisionContext; only `isRevising` stays in the
      // god-state. The action-creator orchestrator dispatches the
      // other two fields to Revision via workflowRevision.setRevisionTarget().
      return {
        ...state,
        isRevising: action.payload.isRevising,
      };

    case ACTIONS.RESET_WORKFLOW:
      // NOTE: backendSessions/isLoadingBackendSessions/tenantId migrated
      // to WorkflowSessionContext (Story 4.2); messages migrated to
      // WorkflowMessagesContext (Story 4.3). Each slice handles its own
      // preserve-on-reset semantics independently. The Story 4.5 cleanup
      // will wire `resetWorkflow` action-creator to also dispatch resets
      // to those slices so the across-slice behaviour is identical to
      // pre-split.
      return {
        ...initialState,
      };

    case ACTIONS.CLEAR_SESSION_FOR_RESTORATION:
      // Clear all session data and UI state to prepare for restoring a different session.
      // Preserves nothing in the god-context residue — the equivalent
      // Session-slice clearing is handled by `restoreInProgressSession`
      // calling `updateBackendSession({...})` with the new session's IDs
      // (the Session slice keeps backendSessions/tenantId intact across
      // restorations because UPDATE_BACKEND_SESSION is a merge, not a
      // replace).
      return {
        ...initialState,
      };

    default:
      return state;
  }
}

// Provider component (inner — receives backendServiceRef as a prop from
// the outer wrapper). The outer WorkflowMachineProvider creates the ref
// once per mount and shares it with WorkflowSessionProvider so both
// providers (and revisionWorkflow) point at the same AthenaBackendService
// instance.
function WorkflowMachineProviderInner({ children, backendServiceRef }) {
  const [state, _innerDispatch] = useReducer(workflowMachineReducer, initialState);

  // Story 4.3: messageCounterRef migrated to WorkflowMessagesContext
  // (it's only used by addMessage which moved with it).
  const stateMachineRef = useRef(new WorkflowStateMachine(state.workflowState));

  // Ref for status polling interval to prevent closure issues
  const statusPollingIntervalRef = useRef(null);

  // Read Session slice (backendSession, tenantId, action-creators) from
  // the WorkflowSessionContext. Session-slice useEffects (tenant-id sync,
  // backend-service sync) live inside that provider — see
  // WorkflowSessionContext.jsx for their byte-for-byte preservation.
  // Named `workflowSession` to avoid shadowing the `session` parameter
  // of `restoreInProgressSession` below.
  const workflowSession = useWorkflowSession();

  // Read Messages slice (action-creators) from WorkflowMessagesContext.
  // Used by restoreInProgressSession + session-persistence restore
  // useEffect, both of which dispatched ADD_MESSAGE/CLEAR_MESSAGES into
  // the god-context pre-Story-4.3.
  const workflowMessages = useWorkflowMessages();

  // Read Results slice (sessionData + action-creators) from
  // WorkflowResultsContext. The per-agent restoration switch in
  // restoreInProgressSession + the contextValue's derived selectors
  // (workflowStatus, currentAgent, completedAgents, agentAwaitingReview)
  // all read from results.sessionData. Story 4.4.
  const workflowResults = useWorkflowResults();

  // Read Revision slice (revisionAgent, revisionStep) from
  // WorkflowRevisionContext. Used by the setRevisionState orchestrator
  // below to fan-out the 3-arg call signature across two slices.
  // Story 4.6.
  const workflowRevision = useWorkflowRevision();

  // Story 4.6 wrap-up: dispatch bridge factory'd out to _dispatchBridge.js.
  // Forwards moved-action dispatches (Story 4.2/4.3/4.4) to the right
  // slice's action-creator and falls through to _innerDispatch otherwise.
  // useMemo (not useCallback) so the factory re-runs when slice hooks
  // change — useCallback would freeze the closure on first render.
  const dispatch = useMemo(
    () => createDispatchBridge({
      innerDispatch: _innerDispatch,
      workflowSession,
      workflowMessages,
      workflowResults,
    }),
    [_innerDispatch, workflowSession, workflowMessages, workflowResults]
  );

  // Use hooks
  const sessionPersistence = useSessionPersistence();
  const stepNavigation = useStepNavigation();
  const revisionWorkflow = useRevisionWorkflow(backendServiceRef);

  // Update state machine when workflow state changes
  useEffect(() => {
    if (stateMachineRef.current.currentState !== state.workflowState) {
      stateMachineRef.current.currentState = state.workflowState;
      stateMachineRef.current.context = state.workflowContext;
    }
  }, [state.workflowState, state.workflowContext]);

  // Story 4.6 wrap-up: persistence restore + save useEffects extracted.
  useSessionPersistenceSync({
    sessionPersistence,
    state,
    workflowSession,
    workflowResults,
    stepNavigation,
    revisionWorkflow,
    backendServiceRef,
    dispatch,
  });

  // Workflow state transitions
  const transitionWorkflowState = useCallback((newState, context = {}) => {
    const result = stateMachineRef.current.transition(newState, context);

    if (result.success) {
      dispatch({
        type: ACTIONS.SET_WORKFLOW_STATE,
        payload: {
          state: result.currentState,
          context: stateMachineRef.current.context
        }
      });
    }

    return result;
  }, []);

  // Session data + result-shape action-creators (updateSessionData,
  // setDocuments, setItemDefinition, setAssets, setThreats,
  // setDamageScenarios, setAttackPaths, setRisks, setRequirements,
  // setCybersecurityGoals, setCybersecurityClaims,
  // setCybersecurityControls, setCybersecurityRequirements, setReport)
  // MIGRATED to WorkflowResultsContext (Story 4.4). Consumers reach
  // them via useWorkflowResults().

  // Clear the agent awaiting review (called when user approves and continues)
  const clearAgentAwaitingReview = useCallback(() => {
    dispatch({
      type: ACTIONS.UPDATE_SESSION_DATA,
      payload: { agent_awaiting_review: null }
    });
  }, []);

  // Message management — MIGRATED to WorkflowMessagesContext (Story 4.3).
  // Action-creators `addMessage`, `updateMessage`, `clearMessages` now
  // live in that provider; consumers reach them via useWorkflowMessages().

  // UI state management
  const showReviewPanel = useCallback((show, data = null) => {
    dispatch({
      type: ACTIONS.SET_SHOW_REVIEW_PANEL,
      payload: { show, data }
    });

    // When showing review panel with agent data, set agent_awaiting_review for permission checks
    if (show && data?.agentName) {
      dispatch({
        type: ACTIONS.UPDATE_SESSION_DATA,
        payload: { agent_awaiting_review: data.agentName }
      });
    }
  }, []);

  const setShowStepComponent = useCallback((show) => {
    dispatch({ type: ACTIONS.SET_SHOW_STEP_COMPONENT, payload: show });
  }, []);

  const setShowReportGenerator = useCallback((show) => {
    dispatch({ type: ACTIONS.SET_SHOW_REPORT_GENERATOR, payload: show });
  }, []);

  // Backend session management — MIGRATED to WorkflowSessionContext
  // (Story 4.2). The action-creators `updateBackendSession`,
  // `fetchBackendSessions`, and `createBackendSession` now live in that
  // provider; consumers reach them via `useWorkflowSession()`. The
  // god-context residue calls them through the `session` hook (see
  // top-of-provider line ~395) when it needs to dispatch from inside
  // restoreInProgressSession.

  // Reset workflow
  const resetWorkflow = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_WORKFLOW });
    stateMachineRef.current.reset();
    stepNavigation.reset();
    revisionWorkflow.clearHistory();
    // Story 6.1: legacy resetSession() class method inlined as ID-bag clear.
    Object.assign(backendServiceRef.current, {
      threadId: null,
      sessionId: null,
      fileId: null,
      tenantId: null,
      isRevisionInProgress: false,
    });
  }, [stepNavigation, revisionWorkflow]);

  // Story 4.6 wrap-up: restoreInProgressSession (~950 LOC) extracted to
  // useSessionRestoration hook. Hook returns the memoized callback for the
  // contextValue spread; signature preserved so consumers see no change.
  const restoreInProgressSession = useSessionRestoration({
    backendServiceRef,
    dispatch,
    workflowSession,
    workflowMessages,
    workflowResults,
    stepNavigation,
    showReviewPanel,
  });


  // Set revision state
  // Story 4.6: setRevisionState is now an orchestrator that fans out
  // across two slices: god-context owns isRevising (per audit §1.5
  // — UI flag consumed by 9 step components); WorkflowRevisionContext
  // owns the revisionAgent + revisionStep target. The 3-arg call
  // signature is preserved for ReviewPanel's 3 callsites.
  const setRevisionState = useCallback((isRevising, revisionAgent = null, revisionStep = null) => {
    dispatch({
      type: ACTIONS.SET_REVISION_STATE,
      payload: { isRevising },
    });
    workflowRevision.setRevisionTarget(revisionAgent, revisionStep);
  }, [workflowRevision]);

  // Workflow status polling — extracted to useStatusPolling hook (Story 4.6 wrap-up).
  // Dispatches UPDATE_SESSION_DATA via the bridge → routes to WorkflowResultsContext.
  useStatusPolling({
    backendServiceRef,
    stateMachineRef,
    statusPollingIntervalRef,
    dispatch,
    threadId: backendServiceRef.current?.threadId,
  });

  // Memoize context value to prevent infinite re-renders.
  // Session-slice fields (backendSession, backendSessions,
  // isLoadingBackendSessions, backendSessionsError, tenantId) and the
  // action-creators updateBackendSession / fetchBackendSessions /
  // createBackendSession have MOVED to WorkflowSessionContext (Story 4.2).
  // Consumers that need them must call useWorkflowSession() — strict cut
  // intentional so the god-context no longer re-renders on Session state
  // changes (Epic 4's per-slice render-isolation goal).
  const contextValue = useMemo(() => ({
    // State
    ...state,
    workflowStateMachine: stateMachineRef.current,
    backendService: backendServiceRef.current,

    // Hooks
    sessionPersistence,
    stepNavigation,
    revisionWorkflow,

    // Story 4.5 strict-cut: the backwards-compat re-exposure block
    // that lived here for stories 4.2-4.4 has been REMOVED. Consumers
    // now reach Session / Messages / Results properties via their
    // dedicated hooks (useWorkflowSession / useWorkflowMessages /
    // useWorkflowResults). The god-context residue exposes only
    // Machine-owned state + computed selectors below.

    // Actions
    transitionWorkflowState,
    clearAgentAwaitingReview,
    // Story 4.3: addMessage / updateMessage / clearMessages migrated to
    // WorkflowMessagesContext; consumers call them via useWorkflowMessages().
    showReviewPanel,
    setShowStepComponent,
    setShowReportGenerator,
    restoreInProgressSession,
    resetWorkflow,
    setRevisionState,

    // Computed
    isProcessing: stateMachineRef.current.isProcessing(),
    requiresUserAction: stateMachineRef.current.requiresUserAction(),
    canResume: stateMachineRef.current.canResume(),
    statusMessage: stateMachineRef.current.getStatusMessage(),

    // Workflow status for reactive permission checks — Story 4.4: read
    // sessionData via useWorkflowResults() (cross-context). The
    // selectors stay on the god-context contextValue so existing
    // consumers (workflow.workflowStatus etc.) continue to work
    // unchanged.
    workflowStatus: workflowResults.sessionData.session_status || 'INITIAL',
    currentAgent: workflowResults.sessionData.current_agent || null,
    completedAgents: workflowResults.sessionData.completed_agents || [],
    agentAwaitingReview: workflowResults.sessionData.agent_awaiting_review || null,
  }), [
    state,
    workflowResults,
    sessionPersistence,
    stepNavigation,
    revisionWorkflow,
    transitionWorkflowState,
    clearAgentAwaitingReview,
    showReviewPanel,
    setShowStepComponent,
    setShowReportGenerator,
    restoreInProgressSession,
    resetWorkflow,
    setRevisionState
  ]);

  return (
    <WorkflowMachineContext.Provider value={contextValue}>
      {children}
    </WorkflowMachineContext.Provider>
  );
}

/**
 * Outer provider wrapper — Story 4.2.
 *
 * Creates `backendServiceRef` (the AthenaBackendService instance,
 * stable across renders) ONCE here and passes it to both
 * WorkflowSessionProvider and WorkflowMachineProviderInner so the same
 * service object is shared. Mount sites (ProjectWorkspace.jsx,
 * AthenaWorkflowOrchestrator.jsx) continue to render
 * `<WorkflowMachineProvider>` unchanged.
 */
// Story 6.1: legacy AthenaBackendService class retired. The ref now holds
// a plain ID-bag — the same fields callers used to set on the class
// instance (threadId/sessionId/fileId/tenantId), plus baseURL (used by
// reviseWorkflow's destructured signature in api/workflow.js) and
// isRevisionInProgress (used by useRevisionWorkflow + useStepWorkflow).
const createBackendServiceRef = () => ({
  threadId: null,
  sessionId: null,
  fileId: null,
  tenantId: null,
  baseURL: config.BACKEND_URL,
  isRevisionInProgress: false,
});

export function WorkflowMachineProvider({ children }) {
  const backendServiceRef = useRef(createBackendServiceRef());

  return (
    <WorkflowSessionProvider backendServiceRef={backendServiceRef}>
      <WorkflowMessagesProvider>
        <WorkflowResultsProvider>
          <WorkflowRevisionProvider>
            <WorkflowMachineProviderInner backendServiceRef={backendServiceRef}>
              {children}
            </WorkflowMachineProviderInner>
          </WorkflowRevisionProvider>
        </WorkflowResultsProvider>
      </WorkflowMessagesProvider>
    </WorkflowSessionProvider>
  );
}

// Hook to use the context
export function useWorkflowMachine() {
  const context = useContext(WorkflowMachineContext);

  if (!context) {
    throw new Error('useWorkflowMachine must be used within WorkflowMachineProvider');
  }

  return context;
}